/* eslint-disable no-lonely-if */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-unsafe-optional-chaining */
import { Box, Flex, Input, Text, Tooltip } from '@chakra-ui/react';
import { ReactGrid } from '@silevis/reactgrid';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Table.scss';
import './DimensionsTableTheme.scss';

import FigmaListTreeIcon from 'components/Icons/FigmaListTreeIcon';
import FigmaInfoIcon from 'components/Icons/FigmaInfoIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import { AddRowCellTemplate } from 'components/AddRowCell';
import { DateCellTemplate } from 'components/DateCell';
import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import { FiltersCellTemplate } from 'components/FiltersCell';
import { HeaderRowTemplate } from 'components/HeaderRow';
import { TextCellTemplate } from 'components/TextCell';
import PlanPermission from 'container/PlanPermission';
import {
  AddDimensionHeader,
  DeleteDimensionHeader,
  DeleteDimensionProperty,
  FetchDimensionGroup,
  FetchDimensionItems,
  FetchDimensionProperties,
  ReorderDimensionProperty,
  UpdateDimensionPropertyBulk,
  UpdateDimensionPropertyName,
  UpdateDimensionValues,
  UpdatePropertyHeader,
  getAddItem,
  getBaseScenario,
  getDimensionProperty,
  getFilteredDimensionItems,
  getLoaderStatus,
  getPropertiesSectionStatus,
  getSelectedDimension,
  getSettingsSectionStatus,
  handleAddItemStatus,
  handleDimensionItemsChange,
  resetBuilderDimension,
  UpdateDimensionName,
  handleDimensionLoader,
  getDimensionLoaderStatus,
  DimensionTableReordering,
} from 'redux/BuilderModeSlice';
import { hasEndDate, hasStartDate } from 'utils/GlobalHelpers';
import { AddItemProperty, getDimensionFilters, handleDimFiltersChange } from 'redux/ItemPageSlice';

import { SelectCellTemplate } from './components/SelectCell';
import { getTableColumns, getTableRows, reorderArray } from './NewTableHelpers';
import SelectionActionBar from './SelectionActionBar';
import { getColumns, getRows } from './TableHelpersopz';
import { DimensionsTableSkeleton } from './DimensionsSkeletons';
import ActiveFiltersPopover from './ActiveFiltersPopover';
import AddPropertyModal from './AddPropertyModal';
import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  radius,
  text as textToken,
} from './designTokens';

// TypeScript interfaces for better type safety
interface Focus {
  rowId: number | string;
  columnId: number | string;
}

interface DeleteModalState {
  itemId: string;
  itemName: string;
  type: string;
  status: boolean;
}

interface GridProperties {
  focusLocation?: Focus;
}

interface TableContainerStyles {
  maxH: string;
  overflow: 'auto' | 'initial';
  overflowY: 'auto' | 'initial' | 'hidden';
  overflowX: 'auto' | 'initial' | 'hidden';
}

interface StickyConfig {
  stickyLeftColumns: number;
  stickyBottomRows: number;
  stickyTopRows: number;
  enableRowSelection: boolean;
  verticalStickyBreakpoint: number;
  horizontalStickyBreakpoint: number;
}

// Add new interfaces for performance optimization
interface ScrollState {
  scrollTop: number;
  scrollLeft: number;
  isScrolling: boolean;
}

interface ViewportState {
  visibleRows: number[];
  visibleColumns: number[];
  isDataVisible: boolean;
}

interface CellTemplates {
  header: HeaderRowTemplate;
  delete: DeleteRowCellTemplate;
  text: TextCellTemplate;
  date: DateCellTemplate;
  addRow: AddRowCellTemplate;
  dropdown: DropdownCellTemplate;
  filter: FiltersCellTemplate;
  select: SelectCellTemplate;
  [key: string]: any; // Add index signature for ReactGrid compatibility
}

// Cell interfaces
interface BaseCell {
  type: string;
  text: string;
  className?: string;
  nonEditable?: boolean;
  hasFormulaError?: boolean;
  index?: number;
  data?: any;
}

interface HeaderCell extends BaseCell {
  type: 'filter';
  options: any[];
  id: string;
  hasDelete: boolean;
  hasEdit: boolean;
  colType?: 'header';
}

interface TextCell extends BaseCell {
  type: 'text';
  placeholder?: string;
  property?: string;
  rows?: number;
  itemmId?: string;
  columnId?: string;
}

interface DateCell extends BaseCell {
  type: 'date';
  date: Date;
}

interface DropdownCell extends BaseCell {
  type: 'dropdown';
  selectOption: boolean;
  selectedValue?: any;
  values: Array<{ label: string; value: string }>;
  operation?: string;
}

interface AddRowCell extends BaseCell {
  type: 'addRow';
}

interface SelectCell extends BaseCell {
  type: 'select';
  placeholder?: string;
  property?: string;
  itemId?: string | number;
  isHeading?: boolean;
}

type Cell = HeaderCell | TextCell | DateCell | DropdownCell | AddRowCell | SelectCell;

// Row interface
interface Row {
  rowId: string | number;
  height: number;
  itemId?: string | number;
  itemName?: string;
  reorderable?: boolean;
  cells: Cell[];
}

// Column interface
interface Column {
  columnId: string;
  name?: string;
  width: number;
  resizable: boolean;
  reorderable?: boolean;
  timeDimId?: string | number;
}
interface TableData {
  id: string | number;
  name: string;
  position?: string;
  [key: string]: any;
}

// Dimension property interface
interface DimensionProperty {
  id: string | number;
  name: string;
  data_format?: string;
  items?: Array<{ name: string; value: string }>;
  [key: string]: any;
}

const swapProperties = (columns: any, target: any, columnIds: any) => {
  const targetId = Number.parseInt(target, 10);
  // Convert columnIds to numbers for comparison
  const idsToSwap = columnIds.map((id: any) => Number.parseInt(id, 10));

  const targetIndex = columns.findIndex((column: any) => column.id === targetId);

  idsToSwap.forEach((id: any) => {
    const swapIndex = columns.findIndex(
      (column: any) => Number.parseInt(column.id, 10) === Number.parseInt(id, 10),
    );
    if (targetIndex !== -1 && swapIndex !== -1) {
      const temporary = columns[targetIndex];
      columns[targetIndex] = columns[swapIndex];
      columns[swapIndex] = temporary;
    }
  });
  return columns;
};

const NewDimensionBuilderTableOpz = ({ planPermission }: any) => {
  const dispatch: any = useDispatch();
  const selectedDimension: any = useSelector(getSelectedDimension);
  const selectedScenario = useSelector(getBaseScenario);
  const filteredDimensionItem: any = useSelector(getFilteredDimensionItems);
  const dimProperties: any = useSelector(getDimensionProperty);
  const loaderStatus: any = useSelector(getLoaderStatus);
  const addItem: any = useSelector(getAddItem);
  const tableReference: any = useRef(null);
  const propertiesSectionStatus = useSelector(getPropertiesSectionStatus);
  const settingsSectionStatus = useSelector(getSettingsSectionStatus);
  // Either side panel eats the same header width, so both compact this row.
  const isSidePanelOpen = propertiesSectionStatus || settingsSectionStatus;
  const dimensionFilters = useSelector(getDimensionFilters);
  const dimensionLoader = useSelector(getDimensionLoaderStatus);
  const [dimensionProperties, setDimensionProperties] = useState<DimensionProperty[]>([]);
  const [isAddColumnOpen, setIsAddColumnOpen] = useState(false);

  // The grid's "+" names the column up front via a modal, then creates it and
  // refetches so the new column appears immediately.
  const handleAddColumn = (columnName: string) => {
    setIsAddColumnOpen(false);
    dispatch(
      AddDimensionHeader({
        dimensionId: selectedDimension?.id,
        data: { name: columnName },
        toaster: true,
      }),
    ).then(() => {
      dispatch(
        FetchDimensionProperties({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    });
  };

  useEffect(() => {
    if (dimProperties) {
      setDimensionProperties(dimProperties);
    }
  }, [dimProperties]);

  const [, setFocus] = useState<Focus>({ rowId: 'header', columnId: 'name' });
  const [tableData, setTableData] = useState<TableData[]>();
  // Use ref to track pending rows to avoid dependency issues in useEffect
  const pendingRowsReference = useRef<TableData[]>([]);
  useEffect(() => {
    const getTableData = async () => {
      if (addItem) {
        dispatch(handleAddItemStatus(false));
      }
      if (selectedDimension && selectedScenario) {
        dispatch(handleDimensionLoader(true));
        await dispatch(
          FetchDimensionProperties({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        ).then((response: any) => {
          if (response?.payload?.properties) {
            response?.payload?.properties?.map((data: any) => {
              if (data?.data_format) {
                dispatch(
                  FetchDimensionGroup({
                    data,
                    parameters: { scenario_id: selectedScenario?.id },
                  }),
                );
              }
              return null;
            });
          }
        });
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
        dispatch(handleDimensionLoader(false));
      }
    };
    getTableData();
    setFocus({ rowId: 'header', columnId: 'name' });
  }, [selectedDimension, selectedScenario]);

  const rows: Row[] = useMemo(() => {
    if (dimensionProperties?.length >= 0 && tableData && tableData.length >= 0) {
      return selectedDimension?.name !== 'Time'
        ? getTableRows(tableData, dimensionProperties, selectedDimension)
        : getRows(tableData, dimensionProperties);
    }
    return [];
  }, [tableData, dimensionProperties, selectedDimension]);

  const memorizedColumns: Column[] = useMemo(() => {
    if (dimensionProperties?.length >= 0 && tableReference?.current?.offsetWidth) {
      return selectedDimension?.name !== 'Time'
        ? getTableColumns(
            dimensionProperties,
            tableReference?.current?.offsetWidth,
            selectedDimension,
          )
        : getColumns(dimensionProperties, tableReference?.current?.offsetWidth);
    }
    return [];
  }, [dimensionProperties, selectedDimension, tableReference?.current?.offsetWidth]);

  // Memoize the start/end date check to avoid recalculation
  const shouldEnableStartEnd = useMemo(() => {
    if (dimensionProperties?.length > 0) {
      const startDate = dimensionProperties.some((item: DimensionProperty) =>
        hasStartDate(item?.name),
      );
      const endDate = dimensionProperties.some((item: DimensionProperty) => hasEndDate(item?.name));
      return startDate || endDate;
    }
    return false;
  }, [dimensionProperties]);

  // It updates dimension properties to enable start and end date toggle
  function updateDimensionStartEnd(enable: boolean) {
    dispatch(
      UpdateDimensionName({
        item_id: selectedDimension?.id,
        request_body: {
          enable_start_end: enable ? 'true' : 'false',
        },
      }),
    );
  }

  // To check if dimensionProperties has start & end date, so enable start and end date toggle in
  useEffect(() => {
    if (shouldEnableStartEnd !== selectedDimension?.enable_start_end) {
      updateDimensionStartEnd(shouldEnableStartEnd);
    }
  }, [shouldEnableStartEnd, selectedDimension?.enable_start_end]);

  const [columns, setColumns] = useState<Column[]>(memorizedColumns);
  const [filtersData, setFiltersData]: any = useState();
  const [gridProperties, setGridProperties] = useState<GridProperties>({});
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    itemId: '',
    itemName: '',
    type: '',
    status: false,
  });

  // Add new state for performance optimization
  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollTop: 0,
    scrollLeft: 0,
    isScrolling: false,
  });

  const [viewportState, setViewportState] = useState<ViewportState>({
    visibleRows: [],
    visibleColumns: [],
    isDataVisible: false,
  });

  const scrollTimeoutReference = useRef<NodeJS.Timeout | null>(null);
  const intersectionObserverReference = useRef<IntersectionObserver | null>(null);
  const tableContainerReference = useRef<HTMLDivElement>(null);

  // Optimize column updates by combining conditions
  useEffect(() => {
    if (tableReference?.current?.offsetWidth && dimensionProperties?.length >= 0) {
      const newColumns: Column[] =
        selectedDimension?.name !== 'Time'
          ? getTableColumns(
              dimensionProperties,
              tableReference?.current?.offsetWidth,
              selectedDimension,
            )
          : getColumns(dimensionProperties, tableReference?.current?.offsetWidth);

      setColumns(newColumns);
    }
  }, [
    propertiesSectionStatus,
    settingsSectionStatus,
    dimensionProperties,
    selectedDimension,
    tableReference?.current?.offsetWidth,
  ]);

  // Column widths are derived from the table's pixel width, which was measured
  // once and then only re-measured when a side panel toggled — so widening the
  // window left the old widths in place and dead space at the right edge.
  //
  // This listens on `window` rather than observing the table element, and that
  // is the whole point: the element's width is itself a *result* of recomputing
  // columns, so a ResizeObserver on it feeds its own output back in as input
  // and the widths oscillate. The window's size has no such dependency.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const onResize = () => {
      clearTimeout(timer);
      // Debounced: a drag-resize emits continuously and rebuilding every
      // column per event is wasted work.
      timer = setTimeout(() => {
        const width = tableReference?.current?.offsetWidth;
        if (!width || !(dimensionProperties?.length >= 0)) return;
        setColumns(
          selectedDimension?.name !== 'Time'
            ? getTableColumns(dimensionProperties, width, selectedDimension)
            : getColumns(dimensionProperties, width),
        );
      }, 150);
    };

    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [dimensionProperties, selectedDimension]);

  useEffect(() => {
    if (filtersData) {
      dispatch(
        handleDimensionItemsChange({
          datas: filtersData?.data,
          cell: filtersData?.cell,
          dimensionFilters,
        }),
      );
    }
  }, [filtersData, dimensionFilters, dispatch]);

  useEffect(() => {
    if (filteredDimensionItem?.length >= 0) {
      // Get current tableData to preserve unsaved rows
      setTableData((currentTableData: TableData[] = []) => {
        // Get all saved item names and IDs from fetched data for matching
        const savedItemsMap = new Map<string, string | number>();
        filteredDimensionItem
          .filter((item: TableData) => item.id && item.id !== '' && item.name)
          .forEach((item: TableData) => {
            const normalizedName = item.name?.toLowerCase().trim() || '';
            if (normalizedName) {
              savedItemsMap.set(normalizedName, item.id);
            }
          });

        // Find rows that are pending (no id or empty id)
        const pendingRowsFromTable = currentTableData.filter(
          (row: TableData) => !row.id || row.id === '',
        );

        // Keep only pending rows that haven't been saved yet
        // A pending row is considered saved if:
        // 1. It has a non-empty name AND
        // 2. That name matches a saved item's name (case-insensitive)
        const remainingPendingRows = pendingRowsFromTable.filter((pendingRow: TableData) => {
          const pendingName = pendingRow.name?.toLowerCase().trim();
          // If name is empty, keep it (it's still pending)
          if (!pendingName) {
            return true;
          }
          // If name matches a saved item, remove it (it's been saved)
          return !savedItemsMap.has(pendingName);
        });

        // Merge fetched data with remaining pending rows
        // Make sure we don't have duplicates by checking IDs
        const fetchedItemIds = new Set(
          filteredDimensionItem
            .filter((item: TableData) => item.id && item.id !== '')
            .map((item: TableData) => item.id),
        );

        const mergedData = [
          ...filteredDimensionItem,
          ...remainingPendingRows.filter(
            (row: TableData) => !row.id || !fetchedItemIds.has(row.id),
          ),
        ];

        // Update pending rows ref
        pendingRowsReference.current = remainingPendingRows;

        return mergedData;
      });
    }
  }, [filteredDimensionItem]);

  // Optimize handleAddRow with useCallback
  const handleAddRow = useCallback(() => {
    const object: TableData = dimensionProperties.reduce(
      (accumulator: any, property: DimensionProperty) => {
        accumulator[property?.id] = '';
        return accumulator;
      },
      { name: '', id: '', position: '' },
    );

    // Add to both tableData and pendingRows
    setTableData((previousData: TableData[] = []) => [...previousData, object]);
    pendingRowsReference.current = [...pendingRowsReference.current, object];
    const newRowIndex = tableData?.length || 0;
    setFocus({ rowId: newRowIndex, columnId: 'name' });
    setGridProperties({ focusLocation: { rowId: newRowIndex, columnId: 'name' } });
    // ReactGrid's focusLocation doesn't know to scroll our own wrapping
    // container into view — a row appended past the fold stayed reachable
    // only by manually scrolling. Scroll to the bottom ourselves once the
    // new row has actually rendered.
    //
    // ReactGrid recalculates its own absolutely-positioned content height in
    // its own post-commit pass (it's a virtualization library, not a plain
    // DOM list), which lands after React's commit but isn't guaranteed to be
    // done by the time a single setTimeout(0) macrotask runs — so reading
    // `scrollHeight` right away could still see the height from *before* the
    // new row was added, landing the scroll one row short. Poll briefly
    // instead of guessing a fixed delay: keep nudging scrollTop to the
    // (possibly still-growing) bottom until it stops moving.
    let lastHeight = -1;
    let attempts = 0;
    const settleScroll = () => {
      const container = tableContainerReference.current;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
      attempts += 1;
      if (container.scrollHeight !== lastHeight && attempts < 15) {
        lastHeight = container.scrollHeight;
        requestAnimationFrame(settleScroll);
      }
    };
    requestAnimationFrame(settleScroll);
  }, [dimensionProperties, tableData?.length]);

  useEffect(() => {
    if (addItem) {
      handleAddRow();
      // Reset on the next tick, not synchronously — addItem must only ever
      // trigger one row per Create-button click (this effect only depends on
      // addItem itself now, so the loop this used to guard against can't
      // recur), but resetting it in the same tick as handleAddRow's
      // focusLocation update was racing ReactGrid's own focus handling, so
      // the first keystroke into the new row's cell was being swallowed.
      setTimeout(() => dispatch(handleAddItemStatus(false)), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addItem]);

  const handleDeleteHeader = (data: any) =>
    setDeleteModal({
      itemId: data?.id || '',
      itemName: data?.text || '',
      type: 'header',
      status: true,
    });

  const handleFilterChange = (cell: any, data: any) => setFiltersData({ cell, data });

  const handleDeleteRow = async (data: any) => {
    if (addItem) {
      dispatch(handleAddItemStatus(false));
    }
    if (data?.id === '' || !data?.id) {
      // Remove from pending rows if it's an unsaved row
      const rowName = data?.name || data?.itemName;
      pendingRowsReference.current = pendingRowsReference.current.filter(
        (row: TableData) => row.name !== rowName && row !== data,
      );
      // Remove from tableData
      setTableData((previousData: TableData[] = []) =>
        previousData.filter((row: TableData) => row.name !== rowName && row !== data),
      );
      dispatch(
        FetchDimensionItems({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    } else {
      setDeleteModal({
        itemId: data?.id || '',
        itemName: data?.itemName || '',
        type: 'row',
        status: true,
      });
    }
  };

  const handleDeleteModal = async (type: string) => {
    if (type === 'header') {
      setDeleteModal({ itemId: '', itemName: '', type: '', status: false });
      dispatch(DeleteDimensionHeader({ item_id: deleteModal?.itemId })).then(() => {
        dispatch(
          FetchDimensionProperties({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      });
    } else {
      if (deleteModal?.itemId) {
        setDeleteModal({ itemId: '', itemName: '', type: '', status: false });
        await dispatch(
          DeleteDimensionProperty({
            item_id: deleteModal?.itemId,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
        dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      }
    }
  };

  const handleContextMenu = (
    selectedRowIds: any,
    selectedColIds: any,
    selectionMode: any,
    menuOptions: any,
    selectedRanges: any,
  ) => {
    const deleteRow = {
      id: 'deleteRow',
      label: 'Delete row',
      handler: () => {
        const filteredRows = rows?.filter((data2: Row) => {
          return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
        });
        if (filteredRows && filteredRows.length > 0) {
          handleDeleteRow({ id: filteredRows[0]?.itemId, itemName: filteredRows[0]?.itemName });
        }
      },
    };

    const deleteColumn = {
      id: 'deleteRow',
      label: 'Delete Column',
      handler: () => {
        const filteredColumn = columns?.filter((data2: Column) => {
          return data2?.columnId === selectedRanges[0][0]?.columnId && data2;
        });
        if (filteredColumn && filteredColumn.length > 0) {
          setDeleteModal({
            itemId: filteredColumn[0]?.columnId || '',
            itemName: filteredColumn[0]?.name || '',
            type: 'header',
            status: true,
          });
        }
      },
    };
    if (
      selectedRanges[0] &&
      selectedRanges[0][0]?.rowId === 'header' &&
      selectedRanges[0][0]?.columnId !== 'name'
    ) {
      return [deleteColumn];
    }
    if (
      selectedRanges[0] &&
      selectedRanges[0][0]?.rowId !== 'header' &&
      selectedRanges[0][0]?.rowId !== 'footer'
    ) {
      let selectedRows = false;
      for (let index = 0; index < selectedRanges[0]?.length; index += 1) {
        selectedRows = selectedRanges[0][0]?.rowId === selectedRanges[0][index]?.rowId;
      }
      if (
        selectedRanges[0][0]?.columnId !== 'delete' &&
        selectedRows &&
        selectedDimension?.name !== 'Time'
      ) {
        menuOptions = menuOptions.filter((object: any) => object.id !== 'cut');
        return [...menuOptions, deleteRow];
      }
      return selectedRanges[0][0]?.columnId === 'delete' &&
        selectedRanges[0]?.length === 1 &&
        selectedDimension?.name !== 'Time'
        ? [deleteRow]
        : menuOptions.filter((object: any) => object.id !== 'cut');
    }
    return [];
  };

  // Memoize sticky configuration for better performance
  const stickyConfig = useMemo<StickyConfig>(
    () => ({
      stickyLeftColumns: columns?.length <= 2 ? 0 : 1,
      stickyBottomRows: 0,
      stickyTopRows: 1,
      enableRowSelection: rows?.length > 3,
      verticalStickyBreakpoint: 100,
      horizontalStickyBreakpoint: 100,
    }),
    [columns?.length, rows?.length],
  );

  // Optimize table container styles for large datasets
  const tableContainerStyles = useMemo<TableContainerStyles>(() => {
    // Always show scrollbars when content overflows, regardless of data size
    const shouldShowVerticalScroll = rows && rows.length > 0;
    const shouldShowHorizontalScroll = columns && columns.length > 0;

    return {
      // Was `calc(95vh - 200px)` — a viewport measurement with no relation to
      // the space this box is actually given. Whenever the real available
      // height was smaller, the scroll area grew past its height-bounded
      // parent (which clips for the rounded border) and the last row was cut
      // off with no scrollbar to reveal it. `100%` + the parent's flex sizing
      // makes the scroll boundary and the clip boundary the same edge.
      maxH: '100%',
      overflow: 'auto', // Always use auto to show scrollbars when needed
      overflowY: shouldShowVerticalScroll ? 'auto' : 'hidden',
      overflowX: shouldShowHorizontalScroll ? 'auto' : 'hidden',
    };
  }, [rows?.length, columns?.length]);

  // Memoize combined grid properties to prevent unnecessary re-renders
  const combinedGridProperties = useMemo(
    () => ({
      ...stickyConfig,
      ...gridProperties,
      disableVirtualScrolling: false, // Enable virtual scrolling for large datasets
      preventClickOnCellSelection: false,
    }),
    [stickyConfig, gridProperties],
  );

  // Memoize cell templates to prevent recreation on every render
  const cellTemplates = useMemo<CellTemplates>(
    () => ({
      header: new HeaderRowTemplate(),
      delete: new DeleteRowCellTemplate(),
      text: new TextCellTemplate(handleDeleteHeader),
      date: new DateCellTemplate(),
      addRow: new AddRowCellTemplate(handleAddRow, planPermission),
      dropdown: new DropdownCellTemplate(handleDeleteRow, planPermission),
      filter: new FiltersCellTemplate(handleFilterChange),
      select: new SelectCellTemplate(),
    }),
    [handleAddRow, planPermission, handleDeleteRow, handleFilterChange, handleDeleteHeader],
  );

  useEffect(() => {
    return () => {
      setFocus({ rowId: 'header', columnId: 'name' });
      pendingRowsReference.current = []; // Clear ref on unmount
      dispatch(resetBuilderDimension());
    };
  }, []);

  const updateHeading = async (data: any) => {
    await dispatch(
      UpdatePropertyHeader({
        propertyId: data?.columnId,
        data: {
          name: data?.newCell?.text,
        },
      }),
    ).then(() => {
      dispatch(
        FetchDimensionProperties({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    });
  };

  const updateCell = async (changes: any) => {
    const change = changes[0];
    const existingRow = rows?.filter((data: any) => {
      return change?.rowId === data?.rowId;
    });
    if (change?.newCell?.colType && change?.previousCell?.colType) {
      updateHeading(change);
    } else if (
      change?.newCell?.property &&
      !change?.previousCell?.text &&
      !existingRow[0]?.itemId
    ) {
      // Update the pending row with the new name before API call
      // The row will be identified and removed from pending after API response by matching name

      await dispatch(
        AddItemProperty({
          dimension_id: Number(selectedDimension?.id),
          request_body: {
            name: change?.newCell?.text,
            scenario_id: selectedScenario?.id,
          },
          toaster: true,
        }),
      );
      await dispatch(
        FetchDimensionItems({
          selectedId: selectedDimension?.id,
          parameters: { scenario_id: selectedScenario?.id },
        }),
      );
    } else {
      if (change?.newCell?.property) {
        await dispatch(
          UpdateDimensionPropertyName({
            item_id: filteredDimensionItem[change?.rowId]?.id,
            name: change?.newCell?.text,
            toaster: true,
          }),
        );
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      } else {
        await dispatch(
          UpdateDimensionValues({
            item_id: filteredDimensionItem[change?.rowId]?.id,
            request_body: {
              property_id: change?.columnId,
              value: change?.newCell?.text,
              scenario_id: selectedScenario?.id,
            },
            toaster: true,
          }),
        );
        await dispatch(
          FetchDimensionItems({
            selectedId: selectedDimension?.id,
            parameters: { scenario_id: selectedScenario?.id },
          }),
        );
      }
    }
  };

  const updateCellBulk = async (changes: any) => {
    const newChanges: any[] = [];
    for (const change of changes) {
      const object = {
        item_id: `${filteredDimensionItem[change?.rowId]?.id}`,
        property_id: `${change?.columnId}`,
        value: change?.newCell?.text,
      };
      newChanges.push(object);
    }
    if (newChanges?.length > 0)
      await dispatch(
        UpdateDimensionPropertyBulk({
          request_body: {
            items: newChanges,
            dimension_id: selectedDimension?.id,
            scenario_id: selectedScenario?.id,
          },
          toaster: true,
        }),
      );
  };

  const updateCells = async (changes: any) => {
    if (addItem) dispatch(handleAddItemStatus(false));

    // Update tableData for pending rows before API call so merge logic can match them correctly
    const change = changes[0];
    if (
      change?.newCell?.property &&
      change?.rowId !== undefined &&
      typeof change.rowId === 'number'
    ) {
      // Check if this is a pending row (row without itemId)
      const existingRow = rows?.find((data: any) => data?.rowId === change?.rowId);
      if (existingRow && !existingRow?.itemId) {
        // rowId directly corresponds to tableData index (header row has rowId: 'header', not a number)
        const rowIndex = change.rowId;
        setTableData((currentTableData: TableData[] = []) => {
          if (rowIndex >= 0 && rowIndex < currentTableData.length) {
            const updatedData = [...currentTableData];
            const rowToUpdate = { ...updatedData[rowIndex] };
            // Update the name property
            if (change.columnId === 'name' || change.newCell.property) {
              rowToUpdate.name = change.newCell.text || '';
            } else if (change.columnId) {
              rowToUpdate[change.columnId] = change.newCell.text || '';
            }
            updatedData[rowIndex] = rowToUpdate;
            return updatedData;
          }
          return currentTableData;
        });
      }
    }

    await (changes?.length > 1 ? updateCellBulk(changes) : updateCell(changes));
    const dimensionFilter = JSON.parse(JSON.stringify(dimensionFilters));
    if (dimensionFilters?.length > 0) {
      for (let index = 0; index < dimensionFilter?.length; index += 1) {
        if (dimensionFilter[index].id === changes[0].columnId) {
          const updatedValue = { name: changes[0]?.newCell?.text, status: true };
          dimensionFilter[index].data = [...dimensionFilter[index]?.data, updatedValue];
        }
      }
      dispatch(
        handleDimensionItemsChange({
          datas: filtersData?.data,
          cell: filtersData?.cell,
          dimensionFilters: dimensionFilter,
        }),
      );

      for (let index = 0; index < dimensionFilter?.length; index += 1) {
        const isCheckedData = dimensionFilter?.map((data: any) => data?.data);
        const isCheckedId = dimensionFilter?.map((data: any) => data?.id);
        dispatch(
          handleDimFiltersChange({
            ischeckedItemsChecked: isCheckedData[index],
            id: isCheckedId[index],
          }),
        );
      }
    }
  };

  const handleLocationChanged = (event: any) => {
    if (Object.keys(gridProperties)?.length > 0) {
      setGridProperties({});
    }
    setFocus(event);
    return true;
  };

  const handleColumnResize = (ci: any, width: number) => {
    setColumns((column: any) => {
      const columnIndex = column.findIndex((element: any) => element.columnId === ci);
      const resizedColumn = column[columnIndex];
      const updatedColumn = { ...resizedColumn, width };
      column[columnIndex] = updatedColumn;
      return [...column];
    });
  };

  const handleCanReorderRows = (targetRowId: any) => {
    return targetRowId !== 'footer' || targetRowId !== 'header';
  };
  const canReorderColumns = (targetColumnId: any) => {
    return targetColumnId !== 'name' || targetColumnId !== 'select';
  };

  const handleColumnsReorder = async (targetColumnId: any, columnIds: any) => {
    if (dimensionProperties?.length) {
      const properties = swapProperties(
        JSON.parse(JSON.stringify(dimensionProperties)),
        targetColumnId,
        columnIds,
      );

      setDimensionProperties([...properties]);

      const reorderedColumsn = properties
        .filter((col: any) => {
          return !Number.isNaN(Number.parseInt(col.id, 10));
        })
        .map((col: any) => {
          return Number.parseInt(col.id, 10);
        });

      dispatch(
        DimensionTableReordering({
          dimId: selectedDimension?.id,
          request_body: { dim_item_prop: reorderedColumsn },
        }),
      );
    }
  };

  const handleRowsReorder = async (targetRowId: any, rowIds: any[]) => {
    if (!tableData) return;

    const to = tableData?.findIndex((person: any) => person.position - 1 === targetRowId);
    const rowsIds = rowIds.map((id) =>
      tableData?.findIndex((person: any) => person.position - 1 === id),
    );
    const reorededArray = reorderArray(tableData, rowsIds, to);
    await dispatch(
      ReorderDimensionProperty({
        selectedDimension: selectedDimension?.id,
        reorededArr: reorededArray,
        scenario_id: selectedScenario?.id,
      }),
    );
    dispatch(
      FetchDimensionItems({
        selectedId: selectedDimension?.id,
        parameters: { scenario_id: selectedScenario?.id },
      }),
    );
  };

  // Performance optimization: Handle scroll events with debouncing
  const handleScroll = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    setScrollState((previous) => ({
      ...previous,
      scrollTop: target.scrollTop,
      scrollLeft: target.scrollLeft,
      isScrolling: true,
    }));

    // Clear existing timeout
    if (scrollTimeoutReference.current) {
      clearTimeout(scrollTimeoutReference.current);
    }

    // Set scrolling to false after scroll ends
    scrollTimeoutReference.current = setTimeout(() => {
      setScrollState((previous) => ({ ...previous, isScrolling: false }));
    }, 150);
  }, []);

  // Performance optimization: Setup intersection observer for lazy loading
  const setupIntersectionObserver = useCallback(() => {
    if (!tableContainerReference.current) return;

    intersectionObserverReference.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setViewportState((previous) => ({ ...previous, isDataVisible: true }));
          } else {
            setViewportState((previous) => ({ ...previous, isDataVisible: false }));
          }
        });
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    intersectionObserverReference.current.observe(tableContainerReference.current);
  }, []);

  // Performance optimization: Handle table focus and data visibility
  const handleTableFocus = useCallback(() => {
    setViewportState((previous) => ({ ...previous, isDataVisible: true }));
  }, []);

  // Performance optimization: Force data refresh when scrolling stops
  const forceDataRefresh = useCallback(() => {
    if (viewportState.isDataVisible && !scrollState.isScrolling) {
      // Trigger a re-render of the table data
      setViewportState((previous) => ({ ...previous, isDataVisible: true }));
    }
  }, [viewportState.isDataVisible, scrollState.isScrolling]);

  // Performance optimization: Setup scroll event listeners and intersection observer
  useEffect(() => {
    if (tableContainerReference.current) {
      setupIntersectionObserver();

      // Add scroll event listener
      const container = tableContainerReference.current;
      container.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (intersectionObserverReference.current) {
          intersectionObserverReference.current.disconnect();
        }
        if (scrollTimeoutReference.current) {
          clearTimeout(scrollTimeoutReference.current);
        }
      };
    }
    return () => {
      if (tableContainerReference.current) {
        tableContainerReference.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [setupIntersectionObserver, handleScroll]);

  // Performance optimization: Force data refresh when scrolling stops
  useEffect(() => {
    if (!scrollState.isScrolling && viewportState.isDataVisible) {
      forceDataRefresh();
    }
  }, [scrollState.isScrolling, viewportState.isDataVisible, forceDataRefresh]);

  const [tableSearchTerm, setTableSearchTerm] = useState('');

  // Search bar was UI-only (state tracked, never applied) — filter the grid's
  // display rows by item name here, but keep `rows` itself untouched since
  // rowId is a raw index into tableData that add/edit/focus logic elsewhere
  // relies on.
  const displayedRows = useMemo(() => {
    if (!tableSearchTerm.trim()) return rows;
    const term = tableSearchTerm.trim().toLowerCase();
    return rows?.filter(
      (row: any) => row?.rowId === 'header' || row?.itemName?.toLowerCase()?.includes(term),
    );
  }, [rows, tableSearchTerm]);

  return (
    <Flex
      height='100%'
      width='100%'
      flexDirection='column'
      ref={tableReference}
      flex={1}
      overflow='hidden'
      position='relative'
      bg={neutral.surface}
      p={4}
      borderRadius={radius.surface}
      border={`1px solid ${neutral.border}`}
    >
      <Flex
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        width='calc(100% + 32px)'
        mx='-16px'
        mt='-16px'
        mb='12px'
        px='16px'
        py='8px'
        gap='12px'
        borderBottom={`1px solid ${neutral.borderSubtle}`}
      >
        <Flex alignItems='center' gap='12px' minW={0}>
          <FigmaListTreeIcon width={18} height={18} color={textToken.secondary} />
          <Tooltip isDisabled label={selectedDimension?.name}>
            <Text
              fontSize={fontSize.subtitle}
              fontWeight='600'
              fontFamily={font.body}
              color={textToken.primary}
              whiteSpace='nowrap'
              overflow='hidden'
              textOverflow='ellipsis'
              maxW='240px'
            >
              {selectedDimension?.name}
            </Text>
          </Tooltip>
          {filteredDimensionItem?.length ? (
            <Flex
              alignItems='center'
              justifyContent='center'
              bg={neutral.surfaceMuted}
              border={`1px solid ${neutral.border}`}
              borderRadius={radius.control}
              px='8px'
              py='2px'
              flexShrink={0}
            >
              <Text
                fontSize={fontSize.label}
                fontWeight='500'
                fontFamily={font.body}
                color={textToken.secondary}
                whiteSpace='nowrap'
              >
                {filteredDimensionItem.length} items
              </Text>
            </Flex>
          ) : null}
          {selectedDimension?.description ? (
            <Tooltip label={selectedDimension.description}>
              <Box display='flex'>
                <FigmaInfoIcon width={14} height={14} color={textToken.muted} />
              </Box>
            </Tooltip>
          ) : null}
        </Flex>
        <Flex alignItems='center' gap='12px' flexWrap='wrap'>
          <Flex
            alignItems='center'
            gap='8px'
            bg={neutral.surface}
            border={`1px solid ${neutral.border}`}
            borderRadius={radius.control}
            px='12px'
            height={controlHeight}
            w={isSidePanelOpen ? '150px' : '219px'}
            flexShrink={1}
            minW='110px'
            transition='border-color 0.12s ease'
            _focusWithin={{ borderColor: accent.border }}
          >
            <FigmaSearchIcon width={13} height={13} color={textToken.muted} />
            <Input
              variant='unstyled'
              placeholder='Search items...'
              value={tableSearchTerm}
              onChange={(event) => setTableSearchTerm(event.target.value)}
              fontSize={fontSize.body}
              fontFamily={font.body}
              color={textToken.primary}
              _placeholder={{ color: textToken.muted }}
              sx={{ '&:focus::placeholder': { opacity: 0 } }}
            />
          </Flex>
          <ActiveFiltersPopover
            dimensionProperties={dimensionProperties}
            setFiltersData={setFiltersData}
            compact={isSidePanelOpen}
          />
          <Flex
            as='button'
            alignItems='center'
            gap='8px'
            bg={neutral.surface}
            border={`1px solid ${neutral.border}`}
            borderRadius={radius.control}
            px={isSidePanelOpen ? '9px' : '12px'}
            height={controlHeight}
            cursor='pointer'
            flexShrink={0}
            transition='background 0.12s ease, border-color 0.12s ease'
            _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
          >
            <FigmaFileDownloadIcon width={14} height={14} color={textToken.secondary} />
            {!isSidePanelOpen && (
              <Text
                fontSize={fontSize.body}
                fontWeight='500'
                fontFamily={font.body}
                color={textToken.primary}
                whiteSpace='nowrap'
              >
                Download
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
      {!planPermission && <SelectionActionBar />}
      {dimensionLoader || loaderStatus ? (
        <DimensionsTableSkeleton />
      ) : (
        <>
          {columns?.length && rows?.length ? (
            <>
              {/* flex='1' minH={0} gives this box a real, bounded height inside
                  the height-bounded root — without it the child's percentage
                  maxH had nothing definite to resolve against and the grid just
                  grew until the root's overflow:hidden cut the last row. */}
              <Box
                position='relative'
                border={`1px solid ${neutral.border}`}
                // Must match the pane/corner-cell radii in
                // DimensionsTableTheme.scss. When the shell clipped at 8px
                // while ReactGrid's corner cells rounded at 7px (and 10px from
                // the shared Table.scss), the mismatch showed as a sliver of
                // double outline at each corner.
                borderRadius={radius.surface}
                overflow='hidden'
                flex='1'
                minH={0}
                display='flex'
                flexDirection='column'
              >
                <Box
                  className='md-table-container sm-scroll dimensions-items-grid'
                  width='100%'
                  flex='1'
                  minH={0}
                  maxH={tableContainerStyles.maxH}
                  overflow={tableContainerStyles.overflow as 'auto' | 'initial'}
                  overflowY={tableContainerStyles.overflowY}
                  overflowX={tableContainerStyles.overflowX}
                  ref={tableContainerReference}
                  onClick={forceDataRefresh}
                  onFocus={handleTableFocus}
                >
                  <ReactGrid
                    rows={displayedRows}
                    columns={columns}
                    enableFillHandle
                    enableRangeSelection
                    enableColumnSelection={true}
                    {...combinedGridProperties}
                    onRowsReordered={handleRowsReorder}
                    onFocusLocationChanging={handleLocationChanged}
                    onCellsChanged={planPermission ? undefined : updateCells}
                    onContextMenu={planPermission ? undefined : handleContextMenu}
                    onColumnResized={planPermission ? undefined : handleColumnResize}
                    canReorderRows={handleCanReorderRows}
                    canReorderColumns={canReorderColumns}
                    onColumnsReordered={handleColumnsReorder}
                    customCellTemplates={cellTemplates}
                  />
                </Box>
                {!planPermission && (
                  <Flex
                    as='button'
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsAddColumnOpen(true);
                    }}
                    position='absolute'
                    top='0'
                    right='0'
                    zIndex={2}
                    // Matches the header row height so the button sits flush
                    // inside it rather than bleeding into the first data row.
                    width='34px'
                    height='34px'
                    bg={accent.solid}
                    borderTopRightRadius={radius.surface}
                    alignItems='center'
                    justifyContent='center'
                    transition='background 0.12s ease'
                    _hover={{ bg: accent.hover }}
                  >
                    <FigmaPlusIcon width={13} height={13} color={textToken.inverse} />
                  </Flex>
                )}
                <AddPropertyModal
                  isOpen={isAddColumnOpen}
                  onClose={() => setIsAddColumnOpen(false)}
                  onCreate={handleAddColumn}
                />
              </Box>
              {!planPermission && (
                <Flex
                  as='button'
                  onClick={handleAddRow}
                  alignItems='center'
                  justifyContent='center'
                  gap='8px'
                  width='100%'
                  mt='12px'
                  py='8px'
                  px='12px'
                  bg='white'
                  border='1px solid #D1D6DD'
                  borderRadius={radius.control}
                  cursor='pointer'
                  flexShrink={0}
                  transition='background 0.15s ease, transform 0.1s ease'
                  _hover={{ bg: '#F5F0FF' }}
                  _active={{ bg: '#F5F0FF', transform: 'scale(0.97)' }}
                >
                  <FigmaPlusIcon width={13} height={13} color='#121826' />
                  <Text
                    fontSize='13px'
                    fontWeight='500'
                    fontFamily='Roboto Medium, Roboto Regular'
                    color='#121826'
                  >
                    Add New item
                  </Text>
                </Flex>
              )}
            </>
          ) : (
            'NA'
          )}
        </>
      )}
      {deleteModal.status && (
        <DeleteGroupModal
          isOpen={deleteModal.status}
          onClose={() => setDeleteModal({ itemId: '', itemName: '', type: '', status: false })}
          itemName={deleteModal.itemName || 'some name'}
          onDelete={() => handleDeleteModal(deleteModal.type)}
          orgChart={true}
        />
      )}
    </Flex>
  );
};

export default PlanPermission(NewDimensionBuilderTableOpz);
